import { CheckCircle2, AlertTriangle, Loader2, Play, Square, Zap } from "lucide-react";

/**
 * Small stats tile. Used in the Admin Media Summaries header strip.
 */
export function Stat({ label, value, tone }) {
  let color = "text-[#0F1829]";
  if (tone === "ok") color = "text-emerald-700";
  else if (tone === "warn") color = "text-amber-700";
  return (
    <div className="p-3 bg-white rounded-xl border border-[#E6E1D6]">
      <p className="text-[10px] uppercase tracking-wider text-[#978D78]">{label}</p>
      <p className={`text-2xl font-semibold ${color}`} style={{ fontFamily: "'Fraunces', serif" }}>{value}</p>
    </div>
  );
}

/**
 * Automatic backfill control panel for the Media Center summary pipeline.
 *
 * Receives the current backfill snapshot (from `/admin/media-summary/backfill/status`)
 * and exposes start/stop controls plus progress, active items and recent history.
 * All state is lifted to the parent page — this component is presentational.
 */
export default function BackfillPanel({
  backfill,
  backfillLimit,
  setBackfillLimit,
  backfillForce,
  setBackfillForce,
  backfillStarting,
  startBackfill,
  stopBackfill,
  remaining,
}) {
  const running = !!backfill?.running;
  const stopRequested = !!backfill?.stop_requested;
  const total = backfill?.total || 0;
  const processed = (backfill?.done || 0) + (backfill?.skipped || 0) + (backfill?.failed || 0);
  const percent = total > 0 ? Math.round((processed / total) * 100) : 0;

  return (
    <div className="p-4 bg-white rounded-xl border border-[#E6E1D6]" data-testid="ms-backfill-panel">
      <Header running={running} remaining={remaining} />
      {!running
        ? <StartForm
            backfillLimit={backfillLimit}
            setBackfillLimit={setBackfillLimit}
            backfillForce={backfillForce}
            setBackfillForce={setBackfillForce}
            backfillStarting={backfillStarting}
            startBackfill={startBackfill}
            remaining={remaining}
          />
        : <RunningView
            backfill={backfill}
            percent={percent}
            processed={processed}
            total={total}
            stopBackfill={stopBackfill}
            stopRequested={stopRequested}
          />
      }
    </div>
  );
}

// ── Header ───────────────────────────────────────────────────────
function Header({ running, remaining }) {
  return (
    <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-[#0F1829]" />
        <h2 className="text-sm font-semibold text-[#0F1829]" style={{ fontFamily: "'Fraunces', serif" }}>
          Backfill automático
        </h2>
        {running && (
          <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">
            <Loader2 size={12} className="animate-spin" /> A correr
          </span>
        )}
      </div>
      <p className="text-xs text-[#6B7280]">
        Processa os conteúdos sequencialmente. Cada item demora ~30-60s. {remaining} por processar nesta categoria.
      </p>
    </div>
  );
}

// ── Start form (visible when not running) ────────────────────────
function StartForm({ backfillLimit, setBackfillLimit, backfillForce, setBackfillForce, backfillStarting, startBackfill, remaining }) {
  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div>
        <label className="block text-[10px] uppercase tracking-wider text-[#978D78] mb-1">Limite (opcional)</label>
        <input
          type="number"
          min="1"
          max="500"
          placeholder="Todos"
          value={backfillLimit}
          onChange={(e) => setBackfillLimit(e.target.value)}
          data-testid="ms-backfill-limit"
          className="w-28 px-3 py-2 text-sm border border-[#E6E1D6] rounded"
        />
      </div>
      <label className="inline-flex items-center gap-2 pb-2 text-xs text-[#0F1829]">
        <input
          type="checkbox"
          checked={backfillForce}
          onChange={(e) => setBackfillForce(e.target.checked)}
          data-testid="ms-backfill-force"
          className="w-4 h-4 accent-[#0F1829]"
        />
        Forçar (regenerar resumos existentes)
      </label>
      <button
        onClick={startBackfill}
        disabled={backfillStarting || remaining === 0}
        data-testid="ms-backfill-start"
        className="ml-auto px-4 py-2 bg-[#0F1829] text-white rounded text-sm inline-flex items-center gap-2 disabled:opacity-50 hover:bg-[#1a2542]"
      >
        {backfillStarting ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
        Iniciar backfill
      </button>
    </div>
  );
}

// ── Running view ─────────────────────────────────────────────────
function RunningView({ backfill, percent, processed, total, stopBackfill, stopRequested }) {
  return (
    <div className="space-y-3">
      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-[#6B7280] mb-1">
          <span>
            {processed} / {total} • {backfill?.done || 0} OK • {backfill?.skipped || 0} ignorados • {backfill?.failed || 0} falha(s)
          </span>
          <span className="font-semibold text-[#0F1829]">{percent}%</span>
        </div>
        <div className="w-full h-2 bg-[#F5F2EB] rounded-full overflow-hidden">
          <div
            className="h-full bg-[#0F1829] transition-all duration-500"
            style={{ width: `${percent}%` }}
            data-testid="ms-backfill-bar"
          />
        </div>
      </div>

      <ActiveItems backfill={backfill} />
      <RecentList recent={backfill?.recent} />

      <div className="flex justify-end">
        <button
          onClick={stopBackfill}
          disabled={stopRequested}
          data-testid="ms-backfill-stop"
          className="px-3 py-1.5 border border-red-300 text-red-700 hover:bg-red-50 rounded text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Square size={12} /> {stopRequested ? "A parar…" : "Parar após este item"}
        </button>
      </div>
    </div>
  );
}

function ActiveItems({ backfill }) {
  if (backfill?.active_items?.length > 0) {
    return (
      <div className="text-xs text-[#6B7280] space-y-1">
        <p className="font-semibold text-[#0F1829]">A processar em paralelo ({backfill.concurrency || 1}):</p>
        {backfill.active_items.map((it) => (
          <p key={`active-${it.id}`} className="flex items-center gap-1.5">
            <Loader2 size={11} className="animate-spin text-amber-600" />
            <span className="font-mono text-[#978D78]">#{it.id}</span>
            <span className="truncate">{it.title}</span>
          </p>
        ))}
      </div>
    );
  }
  if (backfill?.current_id) {
    return (
      <p className="text-xs text-[#6B7280]">
        <Loader2 size={11} className="inline animate-spin mr-1" />
        A processar: <strong className="text-[#0F1829]">#{backfill.current_id}</strong> — {backfill.current_title}
      </p>
    );
  }
  return null;
}

function RecentList({ recent }) {
  if (!recent?.length) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-[#978D78] hover:text-[#0F1829]">Últimos {recent.length} processados</summary>
      <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
        {recent.map((r) => (
          <li key={`rec-${r.id}-${r.ts || ''}`} className="flex items-center gap-2 text-[11px]">
            {r.status === "ok" && <CheckCircle2 size={12} className="text-emerald-600" />}
            {r.status === "skipped" && <CheckCircle2 size={12} className="text-[#978D78]" />}
            {r.status === "failed" && <AlertTriangle size={12} className="text-red-600" />}
            <span className="font-mono text-[#978D78]">#{r.id}</span>
            <span className="flex-1 truncate text-[#0F1829]">{r.title}</span>
            {r.error && <span className="text-red-600 text-[10px] truncate max-w-[30%]" title={r.error}>{r.error.slice(0, 50)}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}
